import { CSSProperties, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentForm from '../PaymentForm';
import useWebsiteBuilder, { WebsiteBuilderStyle } from 'Hooks/useWebsiteBuilder';
import { WebsiteBuilderContent } from 'Hooks/useWebsiteBuilder';
import { styx } from 'modules/Utils';

// Load Stripe outside of component render to avoid recreating the Stripe object on every render
/** Stripe promise instance for payment processing */
const stripePromise = loadStripe('your_publishable_key_here'); // Replace with your actual publishable key

interface PaymentPreviewContentProps {
    contentIndex: number;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    style?: CSSProperties;
    onClickContentBlock?: (index: number) => void;
}

const PaymentPreviewContent = ({
    contentIndex,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    style: styleProp,
    onClickContentBlock,
}: PaymentPreviewContentProps) => {
    // In a real implementation, you would fetch this from your backend
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
    } = useWebsiteBuilder();

    const websiteContent = websiteContentProp || websiteContentState;
    const websiteStyle = websiteStyleProp || websiteStyleState;

    const [clientSecret, setClientSecret] = useState('');

    // Options for the Stripe Elements
    const options = {
        clientSecret,
        appearance: {
            theme: 'stripe' as const,
            // You can customize the appearance further here
        },
    };

    // NOTE: render()
    return (
        <div
            className="paymentPreviewContentComponent"
            style={styx(
                sty.paymentPreviewContentComponent,
                styleProp,
            )}
            onClick={() => onClickContentBlock?.(contentIndex)}
        >
            <h2 style={sty.heading}>Payment Preview</h2>

            {clientSecret ?
                <Elements
                    stripe={stripePromise}
                    options={options}
                >
                    <PaymentForm />
                </Elements>
                :
                <div style={sty.placeholderContainer}>
                    <div style={sty.cardPlaceholder}>
                        <div style={sty.cardNumberField}></div>
                        <div style={sty.cardDetailsRow}>
                            <div style={sty.expiryField}></div>
                            <div style={sty.cvcField}></div>
                        </div>
                    </div>
                    <button style={sty.payButton}>Pay Now</button>
                    <p style={sty.infoText}>
                        This is a payment preview. In production, this would connect to Stripe.
                    </p>
                </div>
            }
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    paymentPreviewContentComponent: {
        paddingLeft: 16,
        paddingRight: 16,
        width: '100%',
        maxWidth: 500,
        margin: '0 auto',
    },
    heading: {
        marginBottom: 24,
        fontSize: 24,
        fontWeight: 600,
    },
    placeholderContainer: {
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        padding: 24,
        backgroundColor: '#f9f9f9',
    },
    cardPlaceholder: {
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 4,
        padding: 16,
        marginBottom: 24,
    },
    cardNumberField: {
        height: 20,
        backgroundColor: '#efefef',
        borderRadius: 4,
        marginBottom: 16,
    },
    cardDetailsRow: {
        display: 'flex',
        gap: 16,
    },
    expiryField: {
        height: 20,
        backgroundColor: '#efefef',
        borderRadius: 4,
        flex: 1,
    },
    cvcField: {
        height: 20,
        backgroundColor: '#efefef',
        borderRadius: 4,
        width: 80,
    },
    payButton: {
        backgroundColor: '#6772e5',
        color: '#ffffff',
        border: 'none',
        borderRadius: 4,
        padding: '12px 16px',
        fontSize: 16,
        fontWeight: 600,
        width: '100%',
        cursor: 'pointer',
    },
    infoText: {
        fontSize: 14,
        color: '#6b7c93',
        textAlign: 'center',
        marginTop: 16,
    }
};

export default PaymentPreviewContent;